<script setup>
import { ref } from 'vue'
import { event } from 'vue-gtag'

//events raised by component
const emit = defineEmits(['closeShareModal'])

//peoperties set by parent
const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	indicator: {
		type: Object,
		required: true,
	}
})	

//reactive field proxies
const sharedUrl = ref('')
const isLinkCopied = ref(false)

//local variables

//methods
function closeModal() {
	emit('closeShareModal');
}

async function copyUrl() {
	const field = document.getElementById('sharedURL');	

	try {
		const clipboardItem = new ClipboardItem({
				'text/plain': field.value
			});
  
		await navigator.clipboard.write([clipboardItem]);
        console.log('Text copied to clipboard successfully!');
		event('share_indicator', { 'indicator' : props.indicator.indicatorName });
		isLinkCopied.value = true
    }
    catch (err) {
        console.error('Failed to copy text: ', err);
		event('error_share', { 'error' : err });
        alert('Failed to copy text. Please try again or copy manually.'); // Optional: handle errors
    }
}

defineExpose({
    openModal
});

function openModal() {
	//selectedTopic.value = ''
	//selectedSubTopic.val"ue = ''
	sharedUrl.value = window.location.href
	isLinkCopied.value = false;
}

</script>

<template>
	<div v-if="props.isOpen" class="modal-overlay">
		<div class="modal-container">
			<div class="modal-header">
				Share
				<button class="btn-icon btn-close" @click="closeModal" aria-label="Close"><i class="fa-solid fa-times"></i></button>
			</div>
			<div class="modal-body">
				<div class="title">
					Copy this link to save your current state of work or share your exact visualization with others.
				</div>
				<div>
					<label for="sharedURL" class="sub-title">
						Dashboard Link
					</label>
					<div>
						<input type="text" id="sharedURL" :value="sharedUrl" readonly>
					</div>
				</div>
			</div>
			<div class="modal-footer">
				<div v-if="isLinkCopied">
					<div class="btn btn-primary"><i class="fa-solid fa-circle-check"></i><span style="padding-left:.5rem;">Link copied!</span></div>
				</div>
				<div v-else>
					<button id="btnCopyLink" class="btn-primary" @click="copyUrl"><i class="fa-regular fa-copy"></i><span style="padding-left:.5rem;">Copy Link</span></button>
				</div>
			</div>
		</div>
	</div>
</template>
   
  <style scoped>
  .modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index:100
  }
  
  .modal-container {
	background-color: var(--surface-color-Default);
	display: flex;
	max-width: 48.25rem;
	/*min-height: 33rem; */
	padding: var(--spacing-0, 0rem);
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-3xl, 2.5rem);
	position: relative;
	z-index: 101;
    border-top-left-radius: var(--radius-xl);
    border-top-right-radius: var(--radius-xl);
    border-bottom-left-radius: var(--radius-xl);
    border-bottom-right-radius: var(--radius-xl);		
  }
  
  .modal-header {
	display: flex;
	height: 5rem;
	padding: 1.4375rem 2rem 1.5625rem 2rem;
	justify-content: space-between;
	align-items: center;
	align-self: stretch;
	border-bottom: 1px solid var(--borders-DividerActive, #064291);
	background: var(--surface-brand-Blue, #064291);
	color: var(--text-Inverse, #fff);
	font-family: var(--font-family-Title);
	font-size: var(--font-size-H5);
    border-top-left-radius: var(--radius-xl);
    border-top-right-radius: var(--radius-xl);	
  }
  
  .modal-body {
	background: var(--surface-Default, #FFF);
	min-height: 15.0625rem;
	display: flex;
	padding: 0rem var(--spacing-xl, 1.5rem) var(--spacing-xl, 6rem) var(--spacing-xl, 1.5rem);
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-xl, 1.5rem);
	align-self: stretch;	
  }
  
  .modal-footer {
	display: flex;
	padding: var(--spacing-lg, 1rem) var(--spacing-xl, 1.5rem);
	justify-content: flex-end;
	align-items: flex-end;
	gap: var(--spacing-lg, 1rem);
	align-self: stretch;
	max-height: 5rem;

	border-top: 1px solid var(--border-color-Divider, #A9B7C7);
	background: var(--surface-default, #FFF);
    border-bottom-left-radius: var(--radius-xl);
    border-bottom-right-radius: var(--radius-xl);		
  }

  .title {
	color: var(--text-Primary, #2C2C2C);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
	display: block;
  }

  .sub-title {
	color: var(--text-secondary, #757575);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
  }

  #sharedURL {
	min-width: 45rem;
	width: 100%;
  }
  
  .btn-close {
	width: 1rem;
	color: var(--icons-icon-cta-reverse, #FFF);
	background: var(--surface-brand-Blue, #064291);
	font-size: 1.25rem;
	font-style: normal;
	font-weight: 900;
	line-height: normal;
	border: 0;
  }

  .btn {
    height: 3rem;
    min-width: 5rem;
    min-height: 2.5rem;
    padding: 0.75rem 0.5rem;
    border-radius: 0.5rem;
    font-family: var(--font-family-Title, Roboto);
    font-size: var(--font-size-Base, 1rem);
    font-style: normal;
    font-weight: 500;
    line-height: var(--font-line-height-Base, 1.5rem);
  }

	/* 499px - Mobile */
	@media (max-width: 31.1875rem) {  

		.modal-overlay {
			position: absolute;
			top: 8.4375rem; /*  135px; */
			left: 0;
			width: calc(100vw);
			height: calc(100vh);
		}


		.modal-container {
			width: 100%;
			height: 100%;
			justify-content: start;
			position: absolute;
			top: 0;
			border-radius: 0;
		}

		.modal-header {
			border-radius: 0;
		}	
	}

	/* 767px - mobile */
	@media (max-width: 47.9375rem) {  
		#sharedURL {
			width: calc(100vw - 3rem);
			min-width: unset;
		}
	}	
</style>